import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IconAiSpark, IconPaperPlane } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Icon } from '../../components/ui/Icon'
import { Input } from '../../components/ui/Input'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { cx } from '../../components/ui/cx'
import { useCartCount } from '../../lib/cart/cart-count-context'
import { addCatalogResultToCart } from '../catalog/catalog-add-to-cart'
import { ResultCard } from '../catalog/components/ResultCard'
import { resultDisplayName } from '../catalog/format'
import { defaultCatalogSearchService } from '../catalog/useCatalogSearch'
import type { CatalogSearchService } from '../catalog/useCatalogSearch'
import type { AgentAgentMessage, AgentFollowUpChip } from './types'
import { useAgentThread } from './useAgentThread'

export interface AgentSearchPageProps {
  /** Injectable for tests (mirrors `CatalogSearchPage`'s `service` prop);
   * defaults to the real BFF-backed catalog search. */
  readonly service?: Pick<CatalogSearchService, 'search'>
}

/**
 * The sourcing-agent thread screen (UID-36, README §7 screen 2 / §13 Epic
 * C1) — a full-page conversational search over "MISUMI catalog + your
 * Fictiv library." Reached either directly (`/agent`) or with a handed-off
 * query from the catalog search no-match state (`/agent?q=...`,
 * `CatalogSearchPage`'s "Ask the sourcing agent" action), which immediately
 * kicks off the first turn.
 *
 * Mirrors `MaterialsAiModal`'s bubble/composer patterns (message rows,
 * sticky Enter-to-send composer, disabled while a turn is in flight) as a
 * full page rather than a modal. The staged "thinking" reveal and the
 * shortlist/summary/follow-up resolution live in `useAgentThread`; this
 * component is presentation + the two user-triggered actions the design
 * calls out specially: a follow-up "ask" chip (new turn, same thread) and
 * the "Add top pick to cart" chip (a direct action, never a new turn).
 */
export function AgentSearchPage({ service = defaultCatalogSearchService }: AgentSearchPageProps) {
  const [searchParams] = useSearchParams()
  const initialQuery = searchParams.get('q')?.trim() ?? ''
  const { messages, ask, busy, reset } = useAgentThread(service)
  const [input, setInput] = useState('')
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  // Guards against re-kicking the first turn on every render (StrictMode
  // double-invoke, etc.) — the same "run once per mount" shape
  // `CatalogSearchPage`'s `lastRunQueryRef` uses, just unconditional here
  // since there's only ever one "initial query" per page mount.
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    if (initialQuery.length > 0) ask(initialQuery)
  }, [initialQuery, ask])

  function send(text: string) {
    const question = text.trim()
    if (!question || busy) return
    setInput('')
    ask(question)
  }

  async function handleAddTopPick(message: AgentAgentMessage) {
    const top = message.shortlist?.[0]
    if (!top) return
    try {
      const result = await addCatalogResultToCart(top.sourceable)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${resultDisplayName(top.sourceable)} to cart`)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    }
  }

  function handleChip(message: AgentAgentMessage, chip: AgentFollowUpChip) {
    if (chip.kind === 'add-to-cart') {
      void handleAddTopPick(message)
      return
    }
    send(chip.ask)
  }

  return (
    <section className="agent-page" aria-labelledby="agent-heading">
      <div className="agent-header">
        <div className="agent-header-identity">
          <span className="agent-avatar" aria-hidden="true">
            <IconAiSpark />
          </span>
          <div>
            <h1 id="agent-heading" className="agent-title">
              Sourcing agent
            </h1>
            <p className="agent-subtitle">
              Searches 46,800 MISUMI SKUs + your Fictiv library and recommends parts.
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={reset}>
            New chat
          </Button>
        )}
      </div>

      {messages.length === 0 ? (
        <EmptyState
          icon={<IconAiSpark />}
          title="What are you sourcing today?"
          detail="Describe the part, material, or spec and I'll put together a ranked shortlist."
        />
      ) : (
        <div className="agent-thread" aria-live="polite">
          {messages.map((message) =>
            message.role === 'user' ? (
              <div key={message.id} className="agent-message-row agent-message-row-user">
                <p className="agent-bubble agent-bubble-user">{message.text}</p>
              </div>
            ) : (
              <AgentTurn key={message.id} message={message} busy={busy} onChip={handleChip} />
            ),
          )}
        </div>
      )}

      <div className="agent-composer">
        <Input
          label="Ask the sourcing agent"
          hideLabel
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              send(input)
            }
          }}
          placeholder="Describe what you're sourcing…"
          disabled={busy}
        />
        <Button aria-label="Send" onClick={() => send(input)} disabled={!input.trim() || busy}>
          <IconPaperPlane />
        </Button>
      </div>
    </section>
  )
}

/** One agent turn: the staged steps, then (once resolved) the summary,
 * shortlist, and follow-up chips. Split out from the page body purely for
 * readability — it owns no state of its own. */
function AgentTurn({
  message,
  busy,
  onChip,
}: {
  readonly message: AgentAgentMessage
  readonly busy: boolean
  readonly onChip: (message: AgentAgentMessage, chip: AgentFollowUpChip) => void
}) {
  const resolved = message.stepDone >= message.steps.length

  return (
    <div className="agent-message-row agent-message-row-agent">
      <span className="agent-avatar agent-avatar-turn" aria-hidden="true">
        <IconAiSpark />
      </span>
      <div className="agent-bubble agent-bubble-agent">
        <ol className="agent-steps">
          {message.steps.map((step, index) => {
            const done = index < message.stepDone
            return (
              <li key={step} className={cx('agent-step', done && 'agent-step-done')}>
                {done ? <Icon name="check" size="sm" /> : <Spinner size="sm" aria-hidden="true" />}
                <span>{step}</span>
              </li>
            )
          })}
        </ol>

        {resolved && (
          <>
            <p className="agent-summary">{message.summary}</p>

            {message.shortlist && message.shortlist.length > 0 && (
              <div className="agent-shortlist">
                {message.shortlist.map((card) => (
                  <ResultCard key={card.sourceable.id} card={card} layout="agent" />
                ))}
              </div>
            )}

            {message.follows.length > 0 && (
              <div className="agent-follow-chips">
                {message.follows.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    className="agent-follow-chip"
                    disabled={busy}
                    onClick={() => onChip(message, chip)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
